import { useMemo } from 'react';
import { UnorderedListOutlined } from '@ant-design/icons';
import { Space, Tag, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { PMP_TAG_COLOR } from '~/app/components/maintenance/pmp-tag-colors';
import { PlanDetailTaskSubtasks } from '../components/PlanDetailTaskSubtasks/PlanDetailTaskSubtasks';
import type { PlanTaskRow } from '../types';

const { Text } = Typography;

export function usePlanDetailTaskColumns(): {
  taskColumns: ColumnsType<PlanTaskRow>;
  taskExpandable: TableProps<PlanTaskRow>['expandable'];
} {
  const { t } = useTranslation('maintenance');

  const taskColumns: ColumnsType<PlanTaskRow> = useMemo(
    () => [
      {
        title: t('common.sequence'),
        dataIndex: 'sequence',
        key: 'sequence',
        width: 72,
      },
      {
        title: t('detail.tasks.name'),
        key: 'task',
        render: (_: unknown, row: PlanTaskRow) => (
          <Space direction="vertical" size={4}>
            <Space size={8} wrap>
              {row.workTypeLabel ? (
                <Tag
                  color={PMP_TAG_COLOR.workType}
                  style={{ marginInlineEnd: 0 }}
                >
                  {row.workTypeLabel}
                </Tag>
              ) : null}
              <Text strong>{row.taskName}</Text>
              {row.subtaskNames.length > 0 ? (
                <Tag
                  icon={<UnorderedListOutlined />}
                  className="plan-detail-subtasks-chip"
                  style={{ marginInlineEnd: 0 }}
                >
                  {t('tasks.subtasks.count', {
                    count: row.subtaskNames.length,
                  })}
                </Tag>
              ) : null}
            </Space>
            {row.parts.length > 0 ? (
              <Space size={[4, 4]} wrap>
                {row.parts.map((part) => (
                  <Tag
                    key={part.id}
                    color={PMP_TAG_COLOR.part}
                    style={{ marginInlineEnd: 0 }}
                  >
                    {part.name}
                  </Tag>
                ))}
              </Space>
            ) : null}
          </Space>
        ),
      },
      {
        title: t('detail.tasks.trigger'),
        dataIndex: 'triggerLabel',
        key: 'triggerLabel',
        render: (label: string) => <Tag color="purple">{label}</Tag>,
      },
    ],
    [t],
  );

  const taskExpandable: TableProps<PlanTaskRow>['expandable'] = useMemo(
    () => ({
      rowExpandable: (row) => row.subtaskNames.length > 0,
      expandRowByClick: false,
      expandedRowClassName: () => 'plan-detail-subtasks-row',
      expandedRowRender: (row) => (
        <PlanDetailTaskSubtasks
          taskId={row.id}
          subtaskNames={row.subtaskNames}
        />
      ),
    }),
    [],
  );

  return { taskColumns, taskExpandable };
}
